import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { trackUpgradeClicked } from '@/lib/analytics';
import SEOHead from '@/components/SEOHead';
import { priceListFor, formatPrice, discountedPrice, DEFAULT_CURRENCY } from '@/config/pricing';
import { usePricing } from '@/hooks/usePricing';
import { Eyebrow, BTN_PRIMARY, BTN_INVERT } from '@/components/bento';
import { PLANS as PLAN_CONFIG } from '@/config/plans';
import { REFERRAL_DISCOUNT } from '@/config/earnings';
import AuthModal from '@/components/AuthModal';
import CheckoutDialog from '@/components/checkout/CheckoutDialog';
import ReferralBar from '@/components/checkout/ReferralBar';
import CreditPacks from '@/components/checkout/CreditPacks';

// ─── Plan definitions ─────────────────────────────────────────────────────────

// `model` and the credit allowances come from builderslab/shared/plans.json — the
// entries services/llm.js routes by and services/planLimits.js grants from — so
// what a card promises is what the account gets. They were typed in by hand
// once, and the Free card advertised 30 credits while the server granted 20.

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    model: PLAN_CONFIG.free.model,
    description: 'All the core features, for free. See what you can build.',
    tier: 0,
    messageCredits: PLAN_CONFIG.free.messageCredits,
    integrationCredits: PLAN_CONFIG.free.integrationCredits,
    features: [
      'Live website preview',
      'Basic templates',
      'Publish on a builderslab.net subdomain',
      'Community support',
    ],
    popular: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    model: PLAN_CONFIG.starter.model,
    description: 'Build your first real projects — freelancers and indie builders.',
    tier: 1,
    messageCredits: PLAN_CONFIG.starter.messageCredits,
    integrationCredits: PLAN_CONFIG.starter.integrationCredits,
    features: [
      'Everything in Free',
      'No "Built with BuildersLab" badge',
      'All templates',
      'Full HTML/CSS/JS code export',
      'Full React.JS code export',
      'Download source files',
      'Credit rollover (up to 1 month)',
    ],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    model: PLAN_CONFIG.pro.model,
    description: 'Go further with your ideas — more credits, more room to build.',
    tier: 2,
    messageCredits: PLAN_CONFIG.pro.messageCredits,
    integrationCredits: PLAN_CONFIG.pro.integrationCredits,
    features: [
      'Everything in Starter',
      '1 custom domain',
      'Priority generation queue',
      'White-label branding',
      'API access',
      'Buy extra credits any time',
    ],
    popular: true,
  },
  {
    id: 'agency',
    name: 'Studio',
    model: PLAN_CONFIG.agency.model,
    description: 'Top credits and dedicated support for teams going all in.',
    tier: 3,
    messageCredits: PLAN_CONFIG.agency.messageCredits,
    integrationCredits: PLAN_CONFIG.agency.integrationCredits,
    features: [
      'Everything in Pro',
      '3 custom domains',
      '3 team seats',
      'Dedicated support',
      'Early access to new features',
    ],
    popular: false,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TIER_RANK = { free: 0, starter: 1, pro: 2, agency: 3 };

function getCtaLabel(planId, userTier) {
  if (!userTier) return planId === 'free' ? 'Get Started Free' : 'Get Started';
  const planRank = TIER_RANK[planId] ?? 0;
  const currentRank = TIER_RANK[userTier] ?? 0;
  if (planRank === currentRank) return 'Current Plan';
  if (planRank > currentRank) return 'Upgrade';
  return 'Downgrade';
}

function isCtaDisabled(planId, userTier) {
  return planId === (userTier ?? 'free');
}


// ─── Main Pricing Component ───────────────────────────────────────────────────

// What the gateway's redirect back means, in words. The verify step sends a
// buyer here with ?error=… or ?pending=1, and nothing on this page used to read
// either — a refused or still-settling payment just landed on the plans again.
const RETURN_MESSAGES = {
  amount_mismatch: 'The payment did not match the price, so the plan was not changed. If you were charged, contact support.',
  payment_not_verified: 'That payment did not go through, so nothing was charged for it. You can try again.',
  payment_failed: 'That payment did not go through. You can try again.',
  missing_meta: 'We could not match that payment to a plan. If you were charged, contact support.',
  server_error: 'Something went wrong confirming that payment. If you were charged, your plan will update shortly — or contact support.',
};

export default function Pricing() {
  const { user } = useAuth();
  const [isYearly, setIsYearly]               = useState(false);
  // Which currency this visitor is quoted in — decided server-side from the
  // request, never by the client. See usePricing for why.
  const pricing = usePricing();
  const [error, setError]                     = useState(null);
  const [notice, setNotice]                   = useState(null);
  const [checkoutPlan, setCheckoutPlan]       = useState(null); // null = closed
  // A referral code applied above the plans: every card shows its price with
  // the discount, and checkout opens with the code already applied.
  const [referral, setReferral]               = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen]  = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err) setError(RETURN_MESSAGES[err] || 'That payment could not be completed. You can try again.');
    if (params.get('pending')) setNotice('Your transfer is being confirmed — your plan updates as soon as it lands.');
    if (err || params.get('pending')) {
      params.delete('error'); params.delete('pending');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  // Close the auth modal automatically once the user signs in
  useEffect(() => {
    if (user && isAuthModalOpen) setIsAuthModalOpen(false);
  }, [user, isAuthModalOpen]);

  // Read the user's current tier from their profile.
  //
  // Not from `user.user_metadata`: that object is a localStorage snapshot taken
  // at sign-in, and publicUser() (server routes/auth.js) only ever puts
  // full_name in it. Reading subscription_tier there always yielded undefined,
  // so every signed-in user rendered as 'free' — paid plans included.
  const [profileTier, setProfileTier] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      setProfileTier(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('subscription_tier')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (!cancelled) setProfileTier(data?.subscription_tier ?? 'free');
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const userTier = user ? profileTier : null;

  const APP_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BuildersLab AI Website Builder',
    applicationCategory: 'WebApplication',
    operatingSystem: 'Web Browser',
    url: 'https://builderslab.net',
    description: 'AI-powered website builder that generates production-ready HTML, CSS & JavaScript in seconds.',
    offers: PLANS.map(p => ({
      '@type': 'Offer',
      name: p.name,
      price: String(priceListFor(DEFAULT_CURRENCY).plans[p.id]?.monthly ?? 0),
      priceCurrency: DEFAULT_CURRENCY,
      description: p.description,
    })),
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white pt-20">
      <SEOHead
        title="Pricing & Plans"
        description="Choose a BuildersLab plan that fits your needs. From free AI generations to unlimited Agency builds with white-label branding and API access."
        ogUrl="https://builderslab.net/Pricing"
        jsonLd={APP_SCHEMA}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">

        {/* ── Header ── */}
        <div className="flex flex-col items-center text-center mb-10">
          <Eyebrow className="self-center">Pricing</Eyebrow>
          <h1 className="mt-5 text-4xl md:text-6xl font-extrabold tracking-[-0.045em] text-white leading-[1.02]">
            Build More, Pay Less
          </h1>
          <p className="mt-4 text-zinc-400 text-lg max-w-2xl mx-auto">
            Start free. Scale when you're ready. Pro and Studio build every site with Claude.
          </p>
        </div>

        {/* ── Billing Toggle ── */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center rounded-full border border-zinc-800 bg-zinc-900 p-1">
            <button
              id="billing-toggle"
              onClick={() => setIsYearly(true)}
              aria-pressed={isYearly}
              className={`px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
                isYearly ? 'bg-white text-zinc-950' : 'text-zinc-400 hover:text-white'
              }`}
            >
              Yearly <span className="font-normal">(+ 2 months free)</span>
            </button>
            <button
              onClick={() => setIsYearly(false)}
              aria-pressed={!isYearly}
              className={`px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
                !isYearly ? 'bg-white text-zinc-950' : 'text-zinc-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div role="alert" className="max-w-2xl mx-auto mb-8 flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-2xl px-5 py-4 text-red-400 text-sm">
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss" className="hover:text-red-300 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {notice && (
          <div role="status" className="max-w-2xl mx-auto mb-8 flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl px-5 py-4 text-amber-300 text-sm">
            <span className="flex-1">{notice}</span>
            <button onClick={() => setNotice(null)} aria-label="Dismiss" className="hover:text-amber-200 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* The referral code, entered above the plans and applied to all of
            them. It used to be mentioned here and typed in at checkout, where
            it was folded away and people did not find it. */}
        <ReferralBar applied={referral} onApplied={setReferral} />

        {/* ── Pricing Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {PLANS.map((plan) => {
            const planPrices = pricing.plans[plan.id];
            const listPrice = planPrices ? (isYearly ? planPrices.yearly : planPrices.monthly) : 0;
            const discounted = !!referral && listPrice > 0;
            const price = discounted ? discountedPrice(listPrice, pricing.currency, referral.discountRate) : listPrice;
            const ctaLabel = getCtaLabel(plan.id, userTier);
            const disabled = isCtaDisabled(plan.id, userTier);

            return (
              <div
                key={plan.id}
                className={`flex flex-col rounded-3xl border bg-zinc-900 p-7 ${
                  plan.popular ? 'border-orange-500 ring-1 ring-orange-500' : 'border-zinc-800'
                }`}
              >
                {/* Name + badge */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="text-2xl font-extrabold tracking-tight text-white">{plan.name}</h3>
                  {plan.popular && (
                    <span className="px-3 py-1 bg-orange-500 text-ink text-xs font-bold rounded-full shrink-0">
                      Popular
                    </span>
                  )}
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6 min-h-[60px]">
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-end gap-1">
                    <span className="text-5xl font-extrabold tracking-[-0.05em] text-white leading-none">
                      {formatPrice(price, pricing)}
                    </span>
                    <span className="text-zinc-500 text-sm mb-0.5">/{isYearly ? 'yr' : 'mo'}</span>
                  </div>
                  {discounted && (
                    <p className="text-xs mt-1.5">
                      <span className="line-through text-zinc-600">{formatPrice(listPrice, pricing)}</span>
                      <span className="text-emerald-400 font-semibold"> {REFERRAL_DISCOUNT} off with {referral.code}</span>
                    </p>
                  )}
                  {isYearly && price > 0 && (
                    <p className="text-zinc-500 text-xs mt-1.5">
                      {formatPrice(Math.round(price / 12), pricing)}/mo billed annually
                    </p>
                  )}
                </div>

                {/* Model + credits */}
                <div className="rounded-2xl bg-zinc-950 border border-zinc-800 p-4 mb-6 space-y-2">
                  {/* A plan with no model name in plans.json names none here,
                      rather than rendering "Built with" and nothing. */}
                  {plan.model && (
                    <p className="text-sm text-zinc-400">
                      Built with <span className="text-white font-semibold">{plan.model}</span>
                    </p>
                  )}
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-white font-semibold">
                      {plan.messageCredits.toLocaleString()} message credits
                    </span>
                    <span className="text-zinc-500 text-xs shrink-0">/mo</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-zinc-300">
                      {plan.integrationCredits.toLocaleString()} form submissions
                    </span>
                    <span className="text-zinc-500 text-xs shrink-0">/mo</span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  id={`cta-${plan.id}`}
                  onClick={() => {
                    if (!user) {
                      setIsAuthModalOpen(true);
                      return;
                    }
                    if (plan.id === 'free') return;
                    trackUpgradeClicked({ planId: plan.id, isYearly });
                    setCheckoutPlan({
                      purpose: 'plan',
                      planId: plan.id,
                      isYearly,
                      title: `${plan.name} plan`,
                      subtitle: isYearly ? 'Yearly' : 'Monthly',
                      // The list price: checkout shows the discount itself,
                      // quoted by the server for this plan.
                      price: listPrice,
                      priceSuffix: isYearly ? '/yr' : '/mo',
                      pricing,
                      referralCode: referral?.code,
                    });
                  }}
                  disabled={disabled}
                  className={`w-full h-12 rounded-full font-bold text-sm mb-7 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    plan.popular ? BTN_PRIMARY : BTN_INVERT
                  }`}
                >
                  {ctaLabel}
                </button>

                {/* Plan highlights */}
                <p className="text-white font-semibold text-sm mb-4">Plan highlights:</p>
                <ul className="space-y-2.5 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm text-zinc-300 leading-snug">
                      <span className="text-orange-400 font-bold shrink-0" aria-hidden="true">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* ── Extra credits ── Pro and Studio can top up; everyone can see what
            it costs before choosing a plan. */}
        <section aria-labelledby="extra-credits" className="mt-16 rounded-3xl border border-zinc-800 bg-zinc-900 p-6 sm:p-8">
          <h2 id="extra-credits" className="sr-only">Extra credits</h2>
          <CreditPacks />
        </section>
      </div>

      {/* ── Checkout ── Flutterwave or crypto, with the referral code field. */}
      {checkoutPlan && (
        <CheckoutDialog purchase={checkoutPlan} onClose={() => setCheckoutPlan(null)} />
      )}

      {/* ── Auth Modal (signed-out users picking a plan) ── */}
      <AuthModal isOpen={isAuthModalOpen} onClose={setIsAuthModalOpen} />
    </div>
  );
}
